/**
 * V5.99.11 — Lightweight i18n for 360 Resto.
 *
 * No external library. We expose:
 *   • <I18nProvider> wraps the app with React context
 *   • useTranslation() → { t, lang, setLang }
 *   • t('dashboard') returns the matching string for the current language
 *
 * Persistence:
 *   • Local: localStorage.lang
 *   • Server (best-effort): PATCH /api/users/me/language with {"language": "fr"}
 *
 * Languages: fr (default) · en · ar (right-to-left)
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from './api';

const API = getApiUrl();

export const SUPPORTED_LANGS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇲🇦', rtl: true },
];

const DICTIONARY = {
  // Navigation / shell
  'nav.home':         { fr: 'Accueil',       en: 'Home',         ar: 'الرئيسية' },
  'nav.planning':     { fr: 'Planning',      en: 'Schedule',     ar: 'الجدول' },
  'nav.inventory':    { fr: 'Inventaire',    en: 'Inventory',    ar: 'المخزون' },
  'nav.team':         { fr: 'Équipe',        en: 'Team',         ar: 'الفريق' },
  'nav.more':         { fr: 'Plus',          en: 'More',         ar: 'المزيد' },

  // App grid (iOS home screen)
  'app.dashboard':    { fr: 'Tableau',       en: 'Dashboard',    ar: 'لوحة' },
  'app.tasks':        { fr: 'Tâches',        en: 'Tasks',        ar: 'المهام' },
  'app.stocks':       { fr: 'Stocks',        en: 'Stocks',       ar: 'المخزون' },
  'app.planning':     { fr: 'Planning',      en: 'Schedule',     ar: 'الجدول' },
  'app.team':         { fr: 'Équipe',        en: 'Team',         ar: 'الفريق' },
  'app.deliveries':   { fr: 'Livraisons',    en: 'Deliveries',   ar: 'التوصيل' },
  'app.orders':       { fr: 'Commandes & factures', en: 'Orders & invoices', ar: 'الطلبات والفواتير' },
  'app.basecamp':     { fr: 'Formation',     en: 'Formation',    ar: 'القاعدة' },
  'app.recipes':      { fr: 'Recettes',      en: 'Recipes',      ar: 'الوصفات' },
  'app.haccp':        { fr: 'HACCP',         en: 'HACCP',        ar: 'HACCP' },
  'app.dlc':          { fr: 'DLC',           en: 'Expiry',       ar: 'صلاحية' },
  'app.training':     { fr: 'Formation',     en: 'Training',     ar: 'تدريب' },
  'app.chat':         { fr: 'Parler à Sabril',       en: 'AI Chat',      ar: 'الذكاء الاصطناعي' },
  'app.assistant':    { fr: 'Assistant ChatGPT',     en: 'ChatGPT Assistant', ar: 'مساعد ChatGPT' },
  'app.subscription': { fr: 'Abonnement',    en: 'Subscription', ar: 'الاشتراك' },
  'app.settings':     { fr: 'Réglages',      en: 'Settings',     ar: 'الإعدادات' },
  'app.docs':         { fr: 'Documents',     en: 'Documents',    ar: 'الوثائق' },
  'app.resto':        { fr: 'Resto',         en: 'Restaurant',   ar: 'المطعم' },
  'app.help':         { fr: 'Aide',          en: 'Help',         ar: 'مساعدة' },
  'app.history':      { fr: 'Histoire',      en: 'Story',        ar: 'قصة' },
  'app.cash':         { fr: 'Caisse',        en: 'Cashier',      ar: 'الصندوق' },
  'app.pos':          { fr: 'Connecter caisse', en: 'Connect POS', ar: 'ربط الصندوق' },
  'app.pro_gestion':  { fr: 'Pro Gestion',    en: 'Pro Billing',   ar: 'الفوترة' },
  'app.integrations': { fr: 'Intégrations',   en: 'Integrations',  ar: 'التكاملات' },
  'app.sales':        { fr: 'Ventes',        en: 'Sales',        ar: 'المبيعات' },
  'app.tickets':      { fr: 'Tickets',       en: 'Receipts',     ar: 'الفواتير' },
  'app.social':       { fr: 'Social media',       en: 'Social',       ar: 'الشبكات' },
  'app.boost':        { fr: 'Booster chiffre',    en: 'Boost Sales',  ar: 'تعزيز' },
  'app.trends':       { fr: 'Tendances',     en: 'Trends',       ar: 'الاتجاهات' },
  'app.evaluation':   { fr: 'Évaluations',   en: 'Reviews',      ar: 'التقييمات' },
  'app.attendance':   { fr: 'Pointage',      en: 'Clock-in',     ar: 'الحضور' },
  'app.leaves':       { fr: 'Congés',        en: 'Leaves',       ar: 'الإجازات' },
  'app.recruitment':  { fr: 'Recrutement',   en: 'Hiring',       ar: 'التوظيف' },
  'app.contracts':    { fr: 'Contrats RH',   en: 'HR Contracts', ar: 'العقود' },
  'app.recurring_purchases': { fr: 'Commandes récurrentes', en: 'Recurring orders', ar: 'المشتريات المتكررة' },

  // Settings page
  'settings.title':           { fr: 'Réglages',           en: 'Settings',         ar: 'الإعدادات' },
  'settings.language':        { fr: 'Langue',             en: 'Language',         ar: 'اللغة' },
  'settings.language.desc':   { fr: 'Choisissez la langue de l\'application', en: 'Choose your app language', ar: 'اختر لغة التطبيق' },
  'settings.language.saved':  { fr: 'Langue mise à jour', en: 'Language updated', ar: 'تم تحديث اللغة' },

  // App grid categories
  'cat.quotidien':    { fr: 'QUOTIDIEN',           en: 'DAILY',                ar: 'اليومي' },
  'cat.equipe':       { fr: 'ÉQUIPE & FORMATION',  en: 'TEAM & TRAINING',      ar: 'الفريق والتدريب' },
  'cat.operations':   { fr: 'OPÉRATIONS',          en: 'OPERATIONS',           ar: 'العمليات' },
  'cat.ventes':       { fr: 'VENTES & MARKETING',  en: 'SALES & MARKETING',    ar: 'المبيعات' },
  'cat.compte':       { fr: 'COMPTE & RÉGLAGES',   en: 'ACCOUNT & SETTINGS',   ar: 'الحساب والإعدادات' },
  'grid.quick_access': { fr: 'ACCÈS RAPIDE',       en: 'QUICK ACCESS',         ar: 'وصول سريع' },
  'grid.see_all':     { fr: 'VOIR TOUT',           en: 'SEE ALL',              ar: 'عرض الكل' },
  'grid.search_app':  { fr: 'Rechercher une app…', en: 'Search an app…',       ar: 'ابحث عن تطبيق…' },

  // Common
  'common.save':       { fr: 'Enregistrer',  en: 'Save',         ar: 'حفظ' },
  'common.cancel':     { fr: 'Annuler',      en: 'Cancel',       ar: 'إلغاء' },
  'common.close':      { fr: 'Fermer',       en: 'Close',        ar: 'إغلاق' },
  'common.delete':     { fr: 'Supprimer',    en: 'Delete',       ar: 'حذف' },
  'common.add':        { fr: 'Ajouter',      en: 'Add',          ar: 'إضافة' },
  'common.edit':       { fr: 'Modifier',     en: 'Edit',         ar: 'تعديل' },
  'common.next':       { fr: 'Suivant',      en: 'Next',         ar: 'التالي' },
  'common.previous':   { fr: 'Précédent',    en: 'Previous',     ar: 'السابق' },
  'common.skip':       { fr: 'Passer',       en: 'Skip',         ar: 'تجاوز' },
  'common.loading':    { fr: 'Chargement…',  en: 'Loading…',     ar: 'جاري التحميل…' },
  'common.search':     { fr: 'Rechercher',   en: 'Search',       ar: 'بحث' },
  'common.welcome':    { fr: 'Bienvenue',    en: 'Welcome',      ar: 'مرحبا' },
  'common.today':      { fr: "Aujourd'hui",  en: 'Today',        ar: 'اليوم' },
  'common.yesterday':  { fr: 'Hier',         en: 'Yesterday',    ar: 'أمس' },
  'common.tomorrow':   { fr: 'Demain',       en: 'Tomorrow',     ar: 'غدا' },
  'common.all':        { fr: 'Tous',         en: 'All',          ar: 'الكل' },
  'common.see_all':    { fr: 'Voir tout',    en: 'See all',      ar: 'عرض الكل' },
  'common.no_data':    { fr: 'Aucune donnée', en: 'No data',     ar: 'لا توجد بيانات' },
  'common.confirm':    { fr: 'Confirmer',    en: 'Confirm',      ar: 'تأكيد' },
  'common.back':       { fr: 'Retour',       en: 'Back',         ar: 'رجوع' },

  // Dashboard / Feed
  'dashboard.greeting':     { fr: 'Bonjour',         en: 'Hello',           ar: 'مرحبا' },
  'dashboard.welcome':      { fr: 'Bienvenue sur votre espace', en: 'Welcome to your space', ar: 'مرحبا بك في مساحتك' },
  'dashboard.quick_access': { fr: 'Accès rapide',    en: 'Quick access',    ar: 'وصول سريع' },
  'dashboard.team':         { fr: 'Équipe',          en: 'Team',            ar: 'الفريق' },
  'dashboard.no_task':      { fr: 'Aucune tâche pour ce jour', en: 'No task for today', ar: 'لا مهام لهذا اليوم' },
  'dashboard.relax':        { fr: 'Profitez-en pour souffler', en: 'Enjoy the break', ar: 'استمتع بالاستراحة' },
  'dashboard.your_story':   { fr: 'Votre story',     en: 'Your story',      ar: 'قصتك' },
  'dashboard.ca_day':       { fr: 'Chiffre du jour',      en: 'Sales today',     ar: 'مبيعات اليوم' },
  'dashboard.weather':      { fr: 'Météo',           en: 'Weather',         ar: 'الطقس' },

  // Tasks
  'tasks.title':       { fr: 'Suivi des tâches',    en: 'Task tracker',     ar: 'متابعة المهام' },
  'tasks.summary':     { fr: 'Résumé du jour',      en: 'Day summary',      ar: 'ملخص اليوم' },
  'tasks.done':        { fr: 'Validées',            en: 'Completed',        ar: 'مكتملة' },
  'tasks.pending':     { fr: 'À faire',             en: 'To do',            ar: 'للقيام به' },
  'tasks.late':        { fr: 'En retard',           en: 'Overdue',          ar: 'متأخرة' },
  'tasks.new':         { fr: 'Nouvelle tâche',      en: 'New task',         ar: 'مهمة جديدة' },
  'tasks.assign':      { fr: 'Assigner',            en: 'Assign',           ar: 'تعيين' },
  'tasks.no_task':     { fr: 'Aucune tâche',        en: 'No task',          ar: 'لا توجد مهام' },
  'tasks.validate':    { fr: 'Valider',             en: 'Validate',         ar: 'تأكيد' },
  'tasks.all_done':    { fr: 'Tout est validé !',   en: 'All done!',        ar: 'كل شيء مكتمل!' },

  // Stocks
  'stocks.title':         { fr: 'Stocks',              en: 'Stocks',           ar: 'المخزون' },
  'stocks.low':           { fr: 'Stocks bas',          en: 'Low stocks',       ar: 'مخزون منخفض' },
  'stocks.critical':      { fr: 'Critique',            en: 'Critical',         ar: 'حرج' },
  'stocks.inventory':     { fr: 'Inventaire',          en: 'Inventory',        ar: 'الجرد' },
  'stocks.add_product':   { fr: 'Ajouter un produit',  en: 'Add product',      ar: 'إضافة منتج' },
  'stocks.quantity':      { fr: 'Quantité',            en: 'Quantity',         ar: 'الكمية' },
  'stocks.unit':          { fr: 'Unité',               en: 'Unit',             ar: 'وحدة' },
  'stocks.supplier':      { fr: 'Fournisseur',         en: 'Supplier',         ar: 'مورد' },
  'stocks.price':         { fr: 'Prix',                en: 'Price',            ar: 'السعر' },
  'stocks.expiry':        { fr: 'DLC',                 en: 'Expiry',           ar: 'صلاحية' },
  'stocks.scan':          { fr: 'Scanner étagère',     en: 'Scan shelf',       ar: 'مسح الرف' },

  // Team
  'team.title':           { fr: 'Mon équipe',          en: 'My team',          ar: 'فريقي' },
  'team.add_employee':    { fr: 'Ajouter un employé',  en: 'Add employee',     ar: 'إضافة موظف' },
  'team.role':            { fr: 'Poste',               en: 'Role',             ar: 'الوظيفة' },
  'team.phone':           { fr: 'Téléphone',           en: 'Phone',            ar: 'الهاتف' },
  'team.email':           { fr: 'Email',               en: 'Email',            ar: 'البريد' },
  'team.hours':           { fr: 'Heures/semaine',      en: 'Hours/week',       ar: 'ساعات/أسبوع' },
  'team.salary':          { fr: 'Salaire',             en: 'Salary',           ar: 'الراتب' },
  'team.invite':          { fr: 'Inviter',             en: 'Invite',           ar: 'دعوة' },
  'team.copy_link':       { fr: 'Copier le lien',      en: 'Copy link',        ar: 'نسخ الرابط' },
  'team.send_whatsapp':   { fr: 'WhatsApp',            en: 'WhatsApp',         ar: 'واتساب' },
  'team.activated':       { fr: 'Activé',              en: 'Active',           ar: 'مفعل' },
  'team.pending':         { fr: 'En attente',          en: 'Pending',          ar: 'في الانتظار' },
};

const I18nContext = createContext({ t: (k) => k, lang: 'fr', setLang: () => {} });

const readInitialLang = () => {
  try {
    const stored = localStorage.getItem('lang');
    if (stored && SUPPORTED_LANGS.some((l) => l.code === stored)) return stored;
  } catch (_e) { /* noop */ }
  // V6.0.83 — Default to FR (target market). Browser detection only catches AR explicitly.
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  if (browser === 'ar') return 'ar';
  return 'fr';
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(readInitialLang);

  // Apply <html lang> + dir for RTL Arabic
  useEffect(() => {
    const meta = SUPPORTED_LANGS.find((l) => l.code === lang);
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', meta?.rtl ? 'rtl' : 'ltr');
  }, [lang]);

  const setLang = useCallback(async (code) => {
    if (!SUPPORTED_LANGS.some((l) => l.code === code)) return;
    setLangState(code);
    try { localStorage.setItem('lang', code); } catch (_e) { /* noop */ }
    // Best-effort server persist — never block the UI on failure
    try {
      await axios.patch(`${API}/users/me/language`, { language: code }, { withCredentials: true });
    } catch (_e) { /* noop */ }
  }, []);

  const t = useCallback((key, fallback) => {
    const row = DICTIONARY[key];
    if (!row) return fallback || key;
    return row[lang] || row.fr || fallback || key;
  }, [lang]);

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => useContext(I18nContext);
